import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { t } from '@lingui/macro';
import { get } from 'lodash';
import Icon from '@material-ui/core/Icon';
import { makeStyles } from '@material-ui/core/styles';
import DataTable from '../../data-table/DataTable';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { SORT_DESC } from '../../../utilities/FormUtilities';
import { useDataService } from '../../../services/ApiDataService';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import { NgpLabelValue } from '../../form/NgpLabelValue';
import AdminPage from '../AdminPage';
import { AdminPages, buildPageId, buildRootPageTarget } from '../AdminLinkUtils';
import { SERVICE_TESTS_LIST_URL } from './TestTypesAdminConstants';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';

const RELOAD_INTERVAL = 60000;
const ERRORED_AGENTS_BASE_URL = '/ngp/v2/service-tests/list-errored-agents/';

const useStyles = makeStyles((theme) => ({
    detailSection: {
        padding: theme.spacing(2),
    },
    testTypeIcon: {
        fontSize: 18,
        verticalAlign: 'middle',
        marginRight: theme.spacing(0.5),
    },
}));

function buildTestDetailsLink(testData) {
    const typeName = encodeURIComponent(get(testData, 'testType.name', ''));
    const testId = encodeURIComponent(get(testData, '_id', ''));
    return `${buildRootPageTarget(
        AdminPages.ServiceTests
    )}/${typeName}/tests/${testId}?returnTo=testTypes.errorDetails`;
}

function buildTypeListLink(testData) {
    const typeGroup = encodeURIComponent(get(testData, 'testType.group', ''));
    return `${buildRootPageTarget(AdminPages.ServiceTests)}/${typeGroup}/tests?pageState=tests-list`;
}

function buildAgentDetailLink(row) {
    const agentId = encodeURIComponent(get(row, 'agent._id', ''));
    return `${buildRootPageTarget(AdminPages.Agents)}/${agentId}`;
}

function formatErrorMessage(row) {
    const messageShort = get(row, 'message_short');
    if (messageShort) {
        return messageShort;
    }
    const avgResponseCode = get(row, 'avgResponseCode');
    if (avgResponseCode) {
        return `Response Code: ${avgResponseCode}`;
    }
    const avgErrorCode = get(row, 'avgErrorCode');
    if (avgErrorCode) {
        return `Error Code: ${avgErrorCode}`;
    }
    return get(row, 'message', '--');
}

function formatVersion(version) {
    if (!version) {
        return '';
    }
    return version.split('-')[0];
}

function buildTestLogLink(testId, row) {
    const agentId = get(row, 'agent._id', '');
    const filterState = `Agent:${agentId}|Test:${testId}`;
    return `/test-log?filterState=${encodeURIComponent(filterState)}`;
}

function getAgentColumns(testId) {
    return [
        {
            type: COLUMN_TYPES.ICON,
            mapping: 'agent.typeIcon',
            csvFormatter: (value, row) => get(row, 'agent.translatedType', ''),
            sortable: true,
            title: t`Type`,
            width: 74,
            icons: [
                {
                    getIcon: (row) => {
                        const icon = get(row, 'agent.typeIcon');
                        return icon ? <Icon>{icon}</Icon> : null;
                    },
                    getTooltip: (row) => get(row, 'agent.translatedType'),
                },
            ],
        },
        {
            type: COLUMN_TYPES.LINK,
            mapping: 'agent.name',
            title: t`Name`,
            sortable: true,
            width: 250,
            link: (row) => ({ target: buildAgentDetailLink(row) }),
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'agent.modelShortDisplay',
            sortable: true,
            title: t`Model`,
            width: 100,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'agent.status',
            sortable: true,
            title: t`Status`,
            width: 100,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'agent.version.softwareVersion',
            sortable: true,
            title: t`nPoint Version`,
            width: 150,
            formatter: (value) => formatVersion(value),
        },
        {
            type: COLUMN_TYPES.LINK,
            mapping: 'message',
            sortable: true,
            title: t`Errors`,
            width: 290,
            mapper: (row) => formatErrorMessage(row),
            link: (row) => ({ target: buildTestLogLink(testId, row) }),
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: 'testTime',
            title: t`Time Run`,
            sortable: true,
            width: 200,
            formatter: FORMATTER_TYPES.DATETIME,
            flex: 1,
        },
    ];
}

const initialSort = [{ property: 'message', direction: SORT_DESC }];

export default function TestErrorDetailPage() {
    const { _id } = useParams();
    const classes = useStyles();
    const dataService = useDataService();
    const [testData, setTestData] = useState(null);

    const goToUrl = buildRootPageTarget(AdminPages.ServiceTests);

    useEffect(() => {
        if (!_id) return;
        dataService
            .get(`${SERVICE_TESTS_LIST_URL}/${_id}`)
            .then((response) => {
                setTestData(get(response, 'test', response));
            })
            .catch(() => {});
    }, [_id, dataService]);

    const agentDataSource = useMemo(() => {
        if (!_id) return null;
        return {
            url: `${ERRORED_AGENTS_BASE_URL}${_id}`,
            totalProperty: 'total',
            dataProperty: 'data',
            params: {
                userGroups: ['00000000-0000-0000-0000-000000000000'],
                searchFields: [
                    'name',
                    'status',
                    'agent',
                    'agent.modelShortDisplay',
                    'message',
                    'message_short',
                    'avgResponseCode',
                    'avgErrorCode',
                    'errorCode',
                ],
                metrics: [
                    'message',
                    'message_short',
                    'message_detail',
                    'avgResponseCode',
                    'avgErrorCode',
                    'errorCode',
                    'testTime',
                ],
            },
        };
    }, [_id]);

    const loader = useQueryLoader(agentDataSource, { disableFilterState: true });
    const { triggerRef } = useIntervalRefreshTrigger({ interval: RELOAD_INTERVAL });
    const columns = useMemo(() => getAgentColumns(_id), [_id]);

    const testName = get(testData, 'name', '');
    const breadcrumbData = useMemo(() => ({ name: testName }), [testName]);

    const breadcrumbs = useBreadcrumb([{ link: goToUrl, text: t`Service Tests` }], _id, breadcrumbData, 'name');

    const testTypeIcon = get(testData, 'testType.details.icon');
    const testTypeTitle = get(testData, 'testType.details.title', '');

    return (
        <AdminPage breadcrumbs={breadcrumbs} pageId={buildPageId(AdminPages.ServiceTests)} actions={<></>}>
            <>
                <div className={classes.detailSection}>
                    <NgpLabelValue
                        dataLabel={t`Test Name`}
                        dataValue={testData ? <a href={buildTestDetailsLink(testData)}>{testName}</a> : '...'}
                        showIfEmpty
                    />
                    <NgpLabelValue
                        dataLabel={t`Test Type`}
                        dataValue={
                            testData ? (
                                <a href={buildTypeListLink(testData)}>
                                    {testTypeIcon && <Icon className={classes.testTypeIcon}>{testTypeIcon}</Icon>}
                                    {testTypeTitle}
                                </a>
                            ) : (
                                '...'
                            )
                        }
                        showIfEmpty
                    />
                </div>

                {loader && (
                    <DataTable
                        tableId="tests-errors-agents"
                        loader={loader}
                        triggerRef={triggerRef}
                        columns={columns}
                        visibleRows={0}
                        disableColumnRemover
                        sorters={initialSort}
                        emptyText={t`No records were found`}
                        selectionOptions={{
                            idMapping: 'agent._id',
                        }}
                    />
                )}
            </>
        </AdminPage>
    );
}
